import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { isTitleLayout } from "@/office/powerpoint/design";
import { hasTitleText, readPresentation } from "@/office/powerpoint/presentation";
import { nichtPruefbar } from "../schnittstelle";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { MAKRO_BILDSCHIRMPRAESENTATION } from "../makros";

export const lektionFolienVerwalten: Lesson = {
  id: "folien-verwalten",
  title: "Folien ordnen und verwalten",
  summary: "Duplizieren, umsortieren, ausblenden – und warum Löschen selten die beste Wahl ist.",
  durationMinutes: 8,
  learningGoals: [
    "Folien in der Foliensortierung umordnen",
    "Folien duplizieren statt neu aufzubauen",
    "Folien ausblenden statt löschen",
  ],
  steps: [
    {
      id: "sortierung",
      title: "Die Foliensortierung",
      explanation: [
        "„Ansicht“ → „Foliensortierung“ zeigt alle Folien als Miniaturen nebeneinander. " +
          "Das ist die Ansicht für die Dramaturgie: Passt die Reihenfolge, ist der " +
          "Spannungsbogen richtig, sind irgendwo drei Folien hintereinander, die " +
          "dasselbe sagen?",
        "Umsortieren geht per Ziehen, sowohl hier als auch in der Miniaturleiste links. " +
          "Mehrere Folien lassen sich mit Strg-Klick einzeln oder mit Umschalt-Klick als " +
          "Block auswählen und gemeinsam bewegen.",
        "Ein häufiger Fehler beim Umsortieren betrifft Folien, deren Inhalt aufeinander " +
          "aufbaut – etwa eine Zahl, die auf der Vorfolie eingeführt wurde. Die " +
          "Foliensortierung zeigt so etwas nicht. Deshalb lohnt es sich, nach jedem " +
          "größeren Umbau einmal durch die Gliederungsansicht zu gehen und zu lesen, ob " +
          "die Titel noch eine Geschichte ergeben.",
      ],
      keyPoints: [
        "Ansicht → Foliensortierung für den Blick aufs Ganze.",
        "Strg-Klick wählt einzelne Folien, Umschalt-Klick einen Block.",
        "Nach dem Umsortieren die Gliederung gegenlesen.",
      ],
      task: {
        instruction:
          "Sorge dafür, dass deine erste Folie das Layout „Titelfolie“ trägt – sie " +
          "eröffnet die Präsentation.",
        hint: "Folie 1 markieren, dann Start → Layout → „Titelfolie“.",
        check: async () => {
          const snapshot = await readPresentation();
          const erste = snapshot.slides[0];

          if (!erste) {
            return fail(
              "In der Präsentation ist noch keine Folie vorhanden.",
              "Lege zuerst eine Folie an (Strg + M).",
            );
          }
          if (!snapshot.features.shapes) {
            return nichtPruefbar(
              "Das Layout einer Folie",
              "1.3",
              "Prüfe unter „Start“ → „Layout“, ob bei Folie 1 die Titelfolie markiert ist.",
            );
          }

          if (isTitleLayout(erste.layoutType, erste.layoutName)) {
            return pass(`Folie 1 steht auf „${erste.layoutName ?? "Titelfolie"}“.`);
          }
          return fail(
            `Folie 1 nutzt das Layout „${erste.layoutName ?? "unbekannt"}“ statt der Titelfolie.`,
            snapshot.features.layoutType
              ? "Folie 1 markieren, dann Start → Layout → „Titelfolie“."
              : "Diese PowerPoint-Version liefert nur den Layoutnamen; gesucht wurde nach „Titelfolie“ beziehungsweise „Title Slide“. " +
                "Bei einem umbenannten Layout kann die Prüfung danebenliegen.",
          );
        },
      },
    },
    {
      id: "duplizieren",
      title: "Duplizieren statt neu aufbauen",
      explanation: [
        "Sobald eine Folie gut sitzt – richtiges Layout, passende Schriftgrößen, ein " +
          "sauber platziertes Diagramm –, ist Strg + D das mit Abstand wirtschaftlichste " +
          "Tastenkürzel in PowerPoint. Die Kopie erbt alles und muss nur noch mit neuem " +
          "Inhalt gefüllt werden.",
        "Vorsicht ist beim Kopieren zwischen zwei Präsentationen geboten. Beim Einfügen " +
          "bietet PowerPoint zwei Möglichkeiten an: „Zielformatierung verwenden“ passt " +
          "die Folie an das Design der Zielpräsentation an – das ist in aller Regel " +
          "richtig. „Ursprüngliche Formatierung beibehalten“ schleppt den kompletten " +
          "Folienmaster der Quellpräsentation mit ein. Danach hat die Datei zwei Master, " +
          "und ein Designwechsel wirkt nur noch auf die Hälfte der Folien.",
        "Wer sich einmal gewundert hat, warum eine Präsentation plötzlich zwei " +
          "verschiedene Schriftarten hat, hat meistens genau das getan.",
      ],
      keyPoints: [
        "Strg + D dupliziert die markierte Folie.",
        "Beim Einfügen aus anderen Dateien „Zielformatierung verwenden“ wählen.",
        "„Ursprüngliche Formatierung beibehalten“ importiert einen zweiten Folienmaster.",
      ],
      task: {
        instruction:
          "Bau deine Präsentation auf mindestens fünf Folien aus – gern über Strg + D – " +
          "und gib auch den neuen Folien einen Titel.",
        hint: "Strg + D dupliziert die markierte Folie samt Layout und Formatierung.",
        check: async () => {
          const snapshot = await readPresentation();
          const anzahl = snapshot.slides.length;

          if (!snapshot.features.shapes) {
            return nichtPruefbar(
              "Die Platzhalter einer Folie",
              "1.3",
              `Zähle selbst nach: Es sollen mindestens fünf Folien sein, jede mit Titel (aktuell gefunden: ${anzahl}).`,
            );
          }

          const mitTitel = snapshot.slides.filter(hasTitleText);
          const ohneTitel = snapshot.slides.filter((folie) => !hasTitleText(folie));

          return fromCriteria(
            [
              criterion(`Mindestens fünf Folien (gefunden: ${anzahl})`, anzahl >= 5),
              criterion(
                ohneTitel.length === 0
                  ? `Alle ${mitTitel.length} Folien haben einen Titel`
                  : `Ohne Titel: Folie ${ohneTitel.map((folie) => folie.index + 1).join(", ")}`,
                anzahl > 0 && ohneTitel.length === 0,
              ),
            ],
            {
              success: `${anzahl} Folien, alle mit Titel – die Präsentation trägt jetzt eine Struktur.`,
              failure: "Noch nicht ganz.",
              hint: "Nach einem Duplikat den Titel anpassen, sonst stehen zwei Folien mit demselben Titel in der Gliederung.",
            },
          );
        },
      },
    },
    {
      id: "ausblenden",
      title: "Ausblenden statt löschen",
      explanation: [
        "Reservefolien sind ein alter Trick guter Vortragender: Zahlen, Details und " +
          "Herleitungen, die im Vortrag zu weit führen würden, aber in der Fragerunde " +
          "gebraucht werden könnten. Sie gehören nicht in den Hauptteil – und sie gehören " +
          "auch nicht gelöscht.",
        "„Bildschirmpräsentation“ → „Folie ausblenden“ nimmt eine Folie aus dem Ablauf, " +
          "ohne sie zu entfernen. Sie bleibt in der Datei, in der Foliensortierung " +
          "erkennbar an der durchgestrichenen Nummer, und lässt sich während der " +
          "Präsentation gezielt aufrufen, indem man ihre Nummer tippt und Enter drückt.",
        "Für den Umgang mit Zwischenständen gilt dasselbe in klein: Eine Folie, bei der " +
          "man unsicher ist, ob sie bleiben soll, wandert ans Ende der Präsentation und " +
          "wird ausgeblendet. Das ist verlässlicher als der Gedanke, man könne sie " +
          "später über die Rückgängig-Funktion wiederholen.",
      ],
      keyPoints: [
        "Bildschirmpräsentation → Folie ausblenden.",
        "Ausgeblendete Folien tragen in der Sortierung eine durchgestrichene Nummer.",
        "Im Vortrag Foliennummer tippen + Enter springt gezielt zu einer Folie.",
      ],
      task: {
        instruction: "Blende mindestens eine Reservefolie am Ende deiner Präsentation aus.",
        hint: "Folie in der Miniaturleiste anklicken, Bildschirmpräsentation → Folie ausblenden.",
        macro: MAKRO_BILDSCHIRMPRAESENTATION,
        check: () =>
          pruefeMitMakro(MAKRO_BILDSCHIRMPRAESENTATION, (bericht) => {
            const ausgeblendet = bericht.zahl("ausgeblendet") ?? 0;
            const folien = bericht.zahl("folien") ?? 0;
            return fromCriteria(
              [
                criterion(`Mindestens eine ausgeblendete Folie (gefunden: ${ausgeblendet})`, ausgeblendet > 0),
                criterion("Es bleiben sichtbare Folien übrig", ausgeblendet < folien),
              ],
              {
                success: "Die Reservefolie ist da, wenn sie gebraucht wird – und stört sonst nicht.",
                failure: "Es ist noch keine Folie ausgeblendet.",
              },
            );
          }),
      },
    },
  ],
};
